import React, { useState, useEffect } from "react";

const ReservationFilters = ({ reservations, onFilter }) => {
  const [tableFilter, setTableFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");

  const uniqueTables = [...new Set(reservations.flatMap(r => r.tables))].sort();

  const getPeriod = (time) => {
    if (!time) return null;
    const [h] = time.split(":").map(Number);
    if (h >= 8 && h < 12) return "jutro";
    if (h >= 12 && h < 20) return "poslijepodne";
    return "vecer";
  };

  useEffect(() => {
    let filtered = reservations;
    if (tableFilter) filtered = filtered.filter(r => r.tables.includes(tableFilter));
    if (periodFilter) filtered = filtered.filter(r => getPeriod(r.start_datetime) === periodFilter);
    onFilter(filtered);
  }, [tableFilter, periodFilter, reservations, onFilter]);

  return (
    <div className="flex gap-2 mt-3">
      <select
        value={tableFilter}
        onChange={(e) => setTableFilter(e.target.value)}
        className="flex-1 bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
      >
        <option value="">Svi stolovi</option>
        {uniqueTables.map(t => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>

      <select
        value={periodFilter}
        onChange={(e) => setPeriodFilter(e.target.value)}
        className="flex-1 bg-gray-800 text-[#c1b428] border border-gray-600 rounded px-2 py-1 text-xs"
      >
        <option value="">Svi termini</option>
        <option value="jutro">☀️ Jutro</option>
        <option value="poslijepodne">🌤️ Poslijepodne</option>
        <option value="vecer">🌙 Večer</option>
      </select>
    </div>
  );
};


const ReservationsModal = ({ date, reservations, onClose }) => {
  const [filteredReservations, setFilteredReservations] = useState(reservations);

  useEffect(() => {
    setFilteredReservations(reservations);
  }, [reservations]);

  const generatePrintHtml = (reservationsToPrint) => {
    const reservationsHtml = reservationsToPrint.map(r => {
      return `
        <div class="card">
          <div class="card-content">
            <div class="text-section">
              <div class="welcome">Dobrodošli!</div>
              <div class="name">${r.guest_name}</div>
              <div class="details">
                <div class="rezervacija">Rezervacija: </div>
                ${r.tables.join(", ")} &nbsp;|&nbsp; ${r.start_datetime} – ${r.end_datetime} &nbsp;|&nbsp; ${r.guests} ${r.guests >= 2 && r.guests <= 4 ? "osobe" : "osoba"}
              </div>
            </div>
            <img src="${window.location.origin}/logo192.png" class="logo" />
          </div>
        </div>
      `;
    }).join("");

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Rezervacije – ${date.toLocaleDateString("hr-HR")}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; }
          .card {
            width: 100%;
            height: 65mm;
            padding: 5mm 12mm;
            border-bottom: 1px dashed #ccc;
            display: flex;
            align-items: center;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .card-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
          }
          .text-section {
            flex: 1;
          }
          .welcome {
            font-size: 11pt;
            font-weight: bold;
            color: #000;
            margin-bottom: 2mm;
          }
          .name {
            font-size: 22pt;
            font-weight: bold;
            margin-bottom: 3mm;
          }
          .details {
            font-size: 11pt;
            color: #333;
          }
          .note {
            font-size: 9pt;
            color: #666;
            margin-top: 2mm;
            font-style: italic;
          }
          .logo {
            width: 25mm;
            height: 25mm;
            object-fit: contain;
            margin-left: 8mm;
            flex-shrink: 0;
          }
        </style>
      </head>
      <body>
        ${reservationsHtml}
      </body>
      </html>
    `;
  };

  const handleDownloadPDF = () => {
    const printWindow = window.open("", "_blank");
    const html = generatePrintHtml(filteredReservations).replace(
      "</style>",
      `
      #pdf-hint {
        position: fixed;
        top: 0; left: 0; right: 0;
        background: #1a1a2e;
        color: #c1b428;
        text-align: center;
        padding: 10px;
        font-family: Arial, sans-serif;
        font-size: 13px;
        font-weight: bold;
        z-index: 9999;
      }
      @media print { #pdf-hint { display: none; } }
      </style>`
    ).replace(
      "<body>",
      `<body>
      <div id="pdf-hint">
        💡 Za PDF: u print dialogu odaberi "Spremi kao PDF" umjesto pisača, zatim klikni Spremi
      </div>`
    );
    
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 600);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    const html = generatePrintHtml(filteredReservations);
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  return (
    <div
      id="modalBackground"
      onClick={(e) => e.target.id === "modalBackground" && onClose()}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
    >
      <div className="bg-gray-900 border border-[#c1b428] rounded-xl shadow-2xl w-[520px] max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="p-4 border-b border-[#c1b428]">
          <h2 className="text-base font-bold text-center text-[#c1b428]">
            {isToday
              ? "Pregled današnjih rezervacija"
              : `Pregled rezervacija za ${date.toLocaleDateString("hr-HR")}`}
          </h2>
          <p className="text-center text-xs text-gray-400 mt-1">
            {reservations.length} rezervacija
          </p>
          <ReservationFilters reservations={reservations} onFilter={setFilteredReservations} />
        </div>

        {/* Lista */}
        <div className="overflow-y-auto flex-1 p-3 space-y-2 print-area">
          {filteredReservations.length > 0 ? (() => {
            const now = new Date();
            const upcoming = filteredReservations.filter(r => {
              if (!isToday || !r.start_datetime) return true;
              const [h, m] = r.start_datetime.split(":").map(Number);
              return (h * 60 + m) >= (now.getHours() * 60 + now.getMinutes());
            });
            const past = filteredReservations.filter(r => {
              if (!isToday || !r.start_datetime) return false;
              const [h, m] = r.start_datetime.split(":").map(Number);
              return (h * 60 + m) < (now.getHours() * 60 + now.getMinutes());
            });
            const allSorted = [...upcoming, ...past];

            const pages = [];
            for (let i = 0; i < allSorted.length; i += 4) {
              pages.push(allSorted.slice(i, i + 4));
            }

            return pages.map((pageReservations, pageIdx) => (
              <div key={pageIdx} className="print-page">
                {pageReservations.map((r) => {
                  const isPast = past.includes(r);
                  return (
                    <div
                      key={r.id}
                      className={`print-card border rounded-lg p-3 text-xs ${
                        isPast ? "bg-gray-900 border-gray-700 opacity-40" : "bg-gray-800 border-gray-700"
                      }`}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-bold px-2 py-0.5 rounded text-xs ${
                          isPast ? "bg-gray-600 text-gray-300" : "bg-[#c1b428] text-black"
                        }`}>
                          {r.tables.join(", ")}
                        </span>
                        <span className="text-gray-300 font-semibold">
                          {r.start_datetime} – {r.end_datetime}
                          {r.shift && <span className="ml-1 text-[#c1b428]">({r.shift})</span>}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm">{r.guest_name}</span>
                        <span className="text-gray-400">👥 {r.guests} {r.guests === 1 ? "osoba" : "osobe"}</span>
                      </div>
                      {r.note && (
                        <div className="mt-1 pt-1 border-t border-gray-700 text-gray-400 italic">
                          📝 {r.note}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ));
          })() : (
            <p className="text-center text-gray-400 py-8">Nema rezervacija za odabrane filtere.</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-[#c1b428] flex justify-between no-print">
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-700 text-[#c1b428] rounded hover:bg-gray-600 text-xs font-semibold"
          >
            Zatvori
          </button>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-1.5 bg-gray-700 text-[#c1b428] border border-[#c1b428] rounded font-bold hover:bg-gray-600 text-xs"
            >
              📄 PDF
            </button>
            <button
              onClick={handlePrint}
              className="px-4 py-1.5 bg-[#c1b428] text-black rounded font-bold hover:bg-yellow-500 text-xs"
            >
              🖨️ Ispis
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ReservationsModal;